import Image from 'next/image'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useMemo} from 'react'
import type {Block} from '@/services/kontent-ai/types'
import {loadImageFromKontentAI} from '@/image-loader'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import type {CtaButtonContentItem} from '../../flexible-web-toolkit/blocks/cta-button'
import {ReusableCTAButtonComponent} from '../../flexible-web-toolkit/blocks/cta-button'

export type PDPFindAVetCardContentItem = IContentItem<{
	buyOrFindAVetCardTitle: Elements.TextElement
	findAVetCardTitle: Elements.TextElement
	findAVetCardImage: Elements.AssetsElement
	findAVetCardDescription: Elements.TextElement
	pdpFindAVetCardCtaButton: Elements.LinkedItemsElement<CtaButtonContentItem>
}>

export const PDPFindAVetCard: Block<PDPFindAVetCardContentItem> = ({
	block,
	BlockMapper,
	globalConfig,
	page,
}) => {
	const {
		findAVetCardTitle,
		findAVetCardImage,
		findAVetCardDescription,
		pdpFindAVetCardCtaButton,
	} = block.elements

	const title = findAVetCardTitle
	const description = findAVetCardDescription
	const image = findAVetCardImage[0]
	const ctaButton = pdpFindAVetCardCtaButton[0]

	const {brandColor} = useMemo(() => {
		const {colorId} = colorWithFallback({
			pageConfig: page.elements.config[0],
		})

		return {brandColor: colorId}
	}, [page.elements.config])

	return (
		<div className="flex h-full flex-col">
			{/* Title */}
			{Boolean(title) && (
				<h2 className="mb-4 text-xl font-semibold text-black">
					{title}
				</h2>
			)}

			{/* Image and Description Section - Horizontal on small screens */}
			<div className="mb-4 flex flex-1 flex-col gap-4 xxs:flex-row xs:flex-row xs:items-start">
				{/* Map/Image Section */}
				{Boolean(image) && (
					<div className="relative h-32 w-full overflow-hidden rounded-lg bg-gray-100 xxs:h-24 xxs:w-24 xs:h-24 xs:w-24 xs:flex-shrink-0">
						<Image
							alt={image?.description || 'Find a vet map'}
							className="object-cover"
							fill
							loader={loadImageFromKontentAI}
							src={image?.url || ''}
						/>
					</div>
				)}

				{/* Description */}
				{Boolean(description) && (
					<div className="flex-1">
						<p className="text-base font-semibold leading-[1.3] text-black">
							{findAVetCardDescription}
						</p>
					</div>
				)}
			</div>

			{/* CTA Button - Fixed at bottom */}
			{Boolean(ctaButton) && ctaButton ? (
				<div className="w-full">
					<ReusableCTAButtonComponent
						BlockMapper={BlockMapper}
						block={ctaButton}
						className="bg-green-700 hover:bg-green-800 w-full rounded px-4 py-3 text-center font-medium text-white transition-colors duration-200"
						globalConfig={globalConfig}
						page={page}
						variant={brandColor ?? undefined}
					/>
				</div>
			) : null}
		</div>
	)
}
